/**
 * admin/queries.ts
 *
 * Read-only Supabase queries for the Stratakey system_admin panel.
 * All functions return { data, error, count? } — callers never need
 * to handle thrown exceptions; errors are logged and returned.
 *
 * Pagination uses Supabase .range(from, to) — page is 1-indexed,
 * pageSize defaults to 20.
 */

import { supabase } from "@/lib/supabase";
import type {
  Profile,
  Organization,
  Unit,
  Host,
  Booking,
  Dispute,
  AuditLog,
  StrataConfig,
  Message,
} from "@/types/database";

// ─── Pagination helpers ───────────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE = 20;

function pageRange(page = 1, pageSize = DEFAULT_PAGE_SIZE): [number, number] {
  const from = (page - 1) * pageSize;
  return [from, from + pageSize - 1];
}

// ─── Response wrapper ─────────────────────────────────────────────────────────

export interface QueryResult<T> {
  data: T | null;
  error: string | null;
  count?: number;
}

export interface QueryListResult<T> {
  data: T[];
  error: string | null;
  count?: number;
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export interface DashboardStats {
  totalUsers: number;
  activeBuildings: number;
  bookingsThisMonth: number;
  mrr: number;
  complianceRate: number;
  openDisputes: number;
  userGrowth: number;
  buildingGrowth: number;
  bookingGrowth: number;
  mrrGrowth: number;
}

export async function getDashboardStats(): Promise<QueryResult<DashboardStats>> {
  try {
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString();

    const [
      totalUsersRes,
      lastMonthUsersRes,
      activeBuildingsRes,
      lastMonthBuildingsRes,
      bookingsThisMonthRes,
      bookingsLastMonthRes,
      subscriptionsRes,
      openDisputesRes,
      complianceBookingsRes,
    ] = await Promise.all([
      supabase.from("profiles").select("id", { count: "exact", head: true }),
      supabase
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .lt("created_at", thisMonthStart),
      supabase
        .from("organizations")
        .select("id", { count: "exact", head: true }),
      supabase
        .from("organizations")
        .select("id", { count: "exact", head: true })
        .lt("created_at", thisMonthStart),
      supabase
        .from("bookings")
        .select("id", { count: "exact", head: true })
        .gte("created_at", thisMonthStart),
      supabase
        .from("bookings")
        .select("id", { count: "exact", head: true })
        .gte("created_at", lastMonthStart)
        .lte("created_at", lastMonthEnd),
      supabase
        .from("subscriptions")
        .select("id, status, price_id")
        .in("status", ["active", "trialing"]),
      supabase
        .from("disputes")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending"),
      supabase
        .from("bookings")
        .select("status")
        .in("status", ["approved", "blocked"]),
    ]);

    // MRR from subscriptions — map price_id to monthly amount
    const PRICE_MAP: Record<string, number> = {
      strata: 18.99,
      property_manager: 28.99,
      individual: 9.99,
    };
    let mrr = 0;
    let lastMonthMrr = 0; // static estimate for growth calc
    (subscriptionsRes.data ?? []).forEach((sub: any) => {
      const priceKey = Object.keys(PRICE_MAP).find((k) =>
        (sub.price_id ?? "").toLowerCase().includes(k)
      );
      if (priceKey) mrr += PRICE_MAP[priceKey];
    });
    // Approximate last-month MRR as 95% of current (conservative growth estimate)
    lastMonthMrr = mrr * 0.95;

    const totalUsers = totalUsersRes.count ?? 0;
    const lastMonthUsers = lastMonthUsersRes.count ?? 0;
    const activeBuildings = activeBuildingsRes.count ?? 0;
    const lastMonthBuildings = lastMonthBuildingsRes.count ?? 0;
    const bookingsThisMonth = bookingsThisMonthRes.count ?? 0;
    const bookingsLastMonth = bookingsLastMonthRes.count ?? 0;
    const openDisputes = openDisputesRes.count ?? 0;

    const bookings = complianceBookingsRes.data ?? [];
    const approved = bookings.filter((b: any) => b.status === "approved").length;
    const total = bookings.length;
    const complianceRate = total > 0 ? Math.round((approved / total) * 1000) / 10 : 100;

    const pctGrowth = (current: number, previous: number) =>
      previous > 0 ? Math.round(((current - previous) / previous) * 1000) / 10 : 0;

    return {
      data: {
        totalUsers,
        activeBuildings,
        bookingsThisMonth,
        mrr: Math.round(mrr * 100) / 100,
        complianceRate,
        openDisputes,
        userGrowth: pctGrowth(totalUsers, lastMonthUsers),
        buildingGrowth: pctGrowth(activeBuildings, lastMonthBuildings),
        bookingGrowth: pctGrowth(bookingsThisMonth, bookingsLastMonth),
        mrrGrowth: pctGrowth(mrr, lastMonthMrr),
      },
      error: null,
    };
  } catch (err: any) {
    console.error("[admin/getDashboardStats]", err);
    return { data: null, error: err.message ?? "Unknown error" };
  }
}

export interface ActivityWithProfile extends AuditLog {
  actor?: Pick<Profile, "id" | "full_name" | "email" | "role" | "avatar_url"> | null;
}

export async function getRecentActivity(limit = 20): Promise<QueryListResult<ActivityWithProfile>> {
  try {
    const { data, error } = await supabase
      .from("audit_logs")
      .select(`
        *,
        actor:profiles!audit_logs_user_id_fkey (
          id, full_name, email, role, avatar_url
        )
      `)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("[admin/getRecentActivity]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as ActivityWithProfile[], error: null };
  } catch (err: any) {
    console.error("[admin/getRecentActivity]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export interface TopBuilding extends Organization {
  booking_count: number;
  compliance_pct: number;
}

export async function getTopBuildings(limit = 10): Promise<QueryListResult<TopBuilding>> {
  try {
    const { data: orgs, error: orgsErr } = await supabase
      .from("organizations")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (orgsErr) {
      console.error("[admin/getTopBuildings]", orgsErr);
      return { data: [], error: orgsErr.message };
    }

    const orgIds = (orgs ?? []).map((o: any) => o.id);
    if (orgIds.length === 0) return { data: [], error: null };

    const { data: bookings, error: bookingsErr } = await supabase
      .from("bookings")
      .select("organization_id, status")
      .in("organization_id", orgIds);

    if (bookingsErr) {
      console.error("[admin/getTopBuildings bookings]", bookingsErr);
      return { data: [], error: bookingsErr.message };
    }

    const result: TopBuilding[] = (orgs ?? []).map((org: any) => {
      const orgBookings = (bookings ?? []).filter((b: any) => b.organization_id === org.id);
      const approved = orgBookings.filter((b: any) => b.status === "approved").length;
      const validated = orgBookings.filter((b: any) =>
        b.status === "approved" || b.status === "blocked"
      ).length;
      return {
        ...org,
        booking_count: orgBookings.length,
        compliance_pct: validated > 0 ? Math.round((approved / validated) * 100) : 100,
      };
    });

    result.sort((a, b) => b.booking_count - a.booking_count);
    return { data: result, error: null };
  } catch (err: any) {
    console.error("[admin/getTopBuildings]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getRecentFailedPayments(limit = 20): Promise<QueryListResult<any>> {
  try {
    const { data, error } = await supabase
      .from("subscriptions")
      .select(`
        *,
        profile:profiles!subscriptions_user_id_fkey (
          id, full_name, email
        )
      `)
      .eq("status", "past_due")
      .order("updated_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("[admin/getRecentFailedPayments]", error);
      return { data: [], error: error.message };
    }
    return { data: data ?? [], error: null };
  } catch (err: any) {
    console.error("[admin/getRecentFailedPayments]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export interface DayCount {
  date: string;
  count: number;
}

export async function getSignupsPerDay(days = 30): Promise<QueryListResult<DayCount>> {
  try {
    const since = new Date();
    since.setDate(since.getDate() - days);

    const { data, error } = await supabase
      .from("profiles")
      .select("created_at")
      .gte("created_at", since.toISOString())
      .order("created_at", { ascending: true });

    if (error) {
      console.error("[admin/getSignupsPerDay]", error);
      return { data: [], error: error.message };
    }

    const counts: Record<string, number> = {};
    (data ?? []).forEach((p: any) => {
      const day = p.created_at.slice(0, 10);
      counts[day] = (counts[day] ?? 0) + 1;
    });

    const result: DayCount[] = Object.entries(counts).map(([date, count]) => ({
      date,
      count,
    }));
    return { data: result, error: null };
  } catch (err: any) {
    console.error("[admin/getSignupsPerDay]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export interface WeekCount {
  week: string;
  count: number;
}

export async function getBookingsPerWeek(weeks = 12): Promise<QueryListResult<WeekCount>> {
  try {
    const since = new Date();
    since.setDate(since.getDate() - weeks * 7);

    const { data, error } = await supabase
      .from("bookings")
      .select("created_at")
      .gte("created_at", since.toISOString())
      .order("created_at", { ascending: true });

    if (error) {
      console.error("[admin/getBookingsPerWeek]", error);
      return { data: [], error: error.message };
    }

    const counts: Record<string, number> = {};
    (data ?? []).forEach((b: any) => {
      const d = new Date(b.created_at);
      const weekStart = new Date(d);
      weekStart.setDate(d.getDate() - d.getDay());
      const key = weekStart.toISOString().slice(0, 10);
      counts[key] = (counts[key] ?? 0) + 1;
    });

    const result: WeekCount[] = Object.entries(counts).map(([week, count]) => ({
      week,
      count,
    }));
    return { data: result, error: null };
  } catch (err: any) {
    console.error("[admin/getBookingsPerWeek]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export interface PlanCount {
  plan: string;
  count: number;
}

export async function getUsersByPlan(): Promise<QueryListResult<PlanCount>> {
  try {
    const { data, error } = await supabase
      .from("subscriptions")
      .select("price_id, status")
      .in("status", ["active", "trialing"]);

    if (error) {
      console.error("[admin/getUsersByPlan]", error);
      return { data: [], error: error.message };
    }

    const counts: Record<string, number> = {};
    (data ?? []).forEach((sub: any) => {
      const priceId: string = sub.price_id ?? "unknown";
      let plan = "unknown";
      if (priceId.toLowerCase().includes("strata")) plan = "strata";
      else if (priceId.toLowerCase().includes("property_manager")) plan = "property_manager";
      else if (priceId.toLowerCase().includes("individual")) plan = "individual";
      counts[plan] = (counts[plan] ?? 0) + 1;
    });

    const result: PlanCount[] = Object.entries(counts).map(([plan, count]) => ({
      plan,
      count,
    }));
    return { data: result, error: null };
  } catch (err: any) {
    console.error("[admin/getUsersByPlan]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export interface MonthMRR {
  month: string;
  mrr: number;
}

export async function getMRRByMonth(months = 12): Promise<QueryListResult<MonthMRR>> {
  try {
    const since = new Date();
    since.setMonth(since.getMonth() - months);

    const { data, error } = await supabase
      .from("subscriptions")
      .select("created_at, price_id, status")
      .gte("created_at", since.toISOString())
      .in("status", ["active", "trialing"])
      .order("created_at", { ascending: true });

    if (error) {
      console.error("[admin/getMRRByMonth]", error);
      return { data: [], error: error.message };
    }

    const PRICE_MAP: Record<string, number> = {
      strata: 18.99,
      property_manager: 28.99,
      individual: 9.99,
    };

    const monthlyMrr: Record<string, number> = {};
    (data ?? []).forEach((sub: any) => {
      const month = sub.created_at.slice(0, 7);
      const priceKey = Object.keys(PRICE_MAP).find((k) =>
        (sub.price_id ?? "").toLowerCase().includes(k)
      );
      const amount = priceKey ? PRICE_MAP[priceKey] : 0;
      monthlyMrr[month] = (monthlyMrr[month] ?? 0) + amount;
    });

    const result: MonthMRR[] = Object.entries(monthlyMrr).map(([month, mrr]) => ({
      month,
      mrr: Math.round(mrr * 100) / 100,
    }));
    return { data: result, error: null };
  } catch (err: any) {
    console.error("[admin/getMRRByMonth]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

// ─── Users ────────────────────────────────────────────────────────────────────

export interface UserFilters {
  search?: string;
  role?: string;
  plan?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
}

export async function getUsers(
  filters: UserFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<Profile>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    let query = supabase
      .from("profiles")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (filters.search) {
      query = query.or(
        `full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`
      );
    }
    if (filters.role) {
      query = query.eq("role", filters.role);
    }
    if (filters.dateFrom) {
      query = query.gte("created_at", filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte("created_at", filters.dateTo);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("[admin/getUsers]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as Profile[], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getUsers]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getUserById(id: string): Promise<QueryResult<Profile>> {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.error("[admin/getUserById]", error);
      return { data: null, error: error.message };
    }
    return { data: data as Profile | null, error: null };
  } catch (err: any) {
    console.error("[admin/getUserById]", err);
    return { data: null, error: err.message ?? "Unknown error" };
  }
}

export async function getUserBookings(
  userId: string,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<Booking>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    // Find all host records for this user, then get their bookings
    const { data: hosts, error: hostsErr } = await supabase
      .from("hosts")
      .select("id")
      .eq("user_id", userId);

    if (hostsErr) {
      console.error("[admin/getUserBookings hosts]", hostsErr);
      return { data: [], error: hostsErr.message };
    }

    const hostIds = (hosts ?? []).map((h: any) => h.id);
    if (hostIds.length === 0) return { data: [], error: null, count: 0 };

    const { data, error, count } = await supabase
      .from("bookings")
      .select(`
        *,
        unit:units ( id, unit_number, organization_id ),
        host:hosts ( id, nights_used, night_limit, status )
      `, { count: "exact" })
      .in("host_id", hostIds)
      .order("created_at", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("[admin/getUserBookings]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as Booking[], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getUserBookings]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getUserBuildings(userId: string): Promise<QueryListResult<Organization>> {
  try {
    const { data, error } = await supabase
      .from("user_assignments")
      .select(`
        organization:organizations (
          id, name, address, total_units, plan, admin_id, created_at, updated_at
        )
      `)
      .eq("user_id", userId);

    if (error) {
      console.error("[admin/getUserBuildings]", error);
      return { data: [], error: error.message };
    }

    const orgs = (data ?? [])
      .map((row: any) => row.organization)
      .filter(Boolean) as Organization[];

    return { data: orgs, error: null };
  } catch (err: any) {
    console.error("[admin/getUserBuildings]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getUserBillingHistory(userId: string): Promise<QueryListResult<any>> {
  try {
    const { data, error } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[admin/getUserBillingHistory]", error);
      return { data: [], error: error.message };
    }
    return { data: data ?? [], error: null };
  } catch (err: any) {
    console.error("[admin/getUserBillingHistory]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getUserAuditTrail(
  userId: string,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<AuditLog>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    const { data, error, count } = await supabase
      .from("audit_logs")
      .select("*", { count: "exact" })
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("[admin/getUserAuditTrail]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as AuditLog[], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getUserAuditTrail]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

// ─── Buildings (organizations) ────────────────────────────────────────────────

export interface BuildingFilters {
  search?: string;
  city?: string;
  plan?: string;
  complianceStatus?: string;
}

export async function getBuildings(
  filters: BuildingFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<Organization>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    let query = supabase
      .from("organizations")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (filters.search) {
      query = query.or(
        `name.ilike.%${filters.search}%,address.ilike.%${filters.search}%`
      );
    }
    if (filters.plan) {
      query = query.eq("plan", filters.plan);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("[admin/getBuildings]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as Organization[], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getBuildings]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getBuildingById(id: string): Promise<QueryResult<Organization>> {
  try {
    const { data, error } = await supabase
      .from("organizations")
      .select(`
        *,
        admin:profiles!organizations_admin_id_fkey (
          id, full_name, email, role
        ),
        config:strata_config ( * )
      `)
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.error("[admin/getBuildingById]", error);
      return { data: null, error: error.message };
    }
    return { data: data as Organization | null, error: null };
  } catch (err: any) {
    console.error("[admin/getBuildingById]", err);
    return { data: null, error: err.message ?? "Unknown error" };
  }
}

export async function getBuildingUnits(
  orgId: string,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<Unit>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    const { data, error, count } = await supabase
      .from("units")
      .select("*", { count: "exact" })
      .eq("organization_id", orgId)
      .order("unit_number", { ascending: true })
      .range(from, to);

    if (error) {
      console.error("[admin/getBuildingUnits]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as Unit[], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getBuildingUnits]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getBuildingBookings(
  orgId: string,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<Booking>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    const { data, error, count } = await supabase
      .from("bookings")
      .select(`
        *,
        unit:units ( id, unit_number ),
        host:hosts (
          id, nights_used, night_limit, status,
          profile:profiles!hosts_user_id_fkey ( id, full_name, email )
        )
      `, { count: "exact" })
      .eq("organization_id", orgId)
      .order("created_at", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("[admin/getBuildingBookings]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as Booking[], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getBuildingBookings]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getBuildingHosts(orgId: string): Promise<QueryListResult<Host>> {
  try {
    const { data, error } = await supabase
      .from("hosts")
      .select(`
        *,
        profile:profiles!hosts_user_id_fkey ( id, full_name, email, avatar_url ),
        unit:units ( id, unit_number, status )
      `)
      .eq("organization_id", orgId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[admin/getBuildingHosts]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as Host[], error: null };
  } catch (err: any) {
    console.error("[admin/getBuildingHosts]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

// ─── Units ────────────────────────────────────────────────────────────────────

export interface UnitFilters {
  search?: string;
  buildingId?: string;
  status?: string;
}

export async function getUnits(
  filters: UnitFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<Unit>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    let query = supabase
      .from("units")
      .select(`
        *,
        organization:organizations ( id, name )
      `, { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (filters.search) {
      query = query.or(
        `unit_number.ilike.%${filters.search}%,owner_name.ilike.%${filters.search}%,owner_email.ilike.%${filters.search}%`
      );
    }
    if (filters.buildingId) {
      query = query.eq("organization_id", filters.buildingId);
    }
    if (filters.status) {
      query = query.eq("status", filters.status);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("[admin/getUnits]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as Unit[], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getUnits]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getUnitById(id: string): Promise<QueryResult<Unit>> {
  try {
    const { data, error } = await supabase
      .from("units")
      .select(`
        *,
        organization:organizations ( id, name, address, plan ),
        host:hosts (
          id, status, nights_used, night_limit, expires_at,
          profile:profiles!hosts_user_id_fkey ( id, full_name, email )
        )
      `)
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.error("[admin/getUnitById]", error);
      return { data: null, error: error.message };
    }
    return { data: data as Unit | null, error: null };
  } catch (err: any) {
    console.error("[admin/getUnitById]", err);
    return { data: null, error: err.message ?? "Unknown error" };
  }
}

export async function getUnitBookings(
  unitId: string,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<Booking>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    const { data, error, count } = await supabase
      .from("bookings")
      .select(`
        *,
        host:hosts (
          id, nights_used, night_limit,
          profile:profiles!hosts_user_id_fkey ( id, full_name, email )
        )
      `, { count: "exact" })
      .eq("unit_id", unitId)
      .order("check_in", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("[admin/getUnitBookings]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as Booking[], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getUnitBookings]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

// ─── Bookings ─────────────────────────────────────────────────────────────────

export interface BookingFilters {
  search?: string;
  status?: string;
  buildingId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export async function getBookings(
  filters: BookingFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<Booking>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    let query = supabase
      .from("bookings")
      .select(`
        *,
        unit:units ( id, unit_number, organization_id ),
        host:hosts (
          id, nights_used, night_limit, status,
          profile:profiles!hosts_user_id_fkey ( id, full_name, email )
        )
      `, { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (filters.search) {
      query = query.or(
        `guest_name.ilike.%${filters.search}%,guest_email.ilike.%${filters.search}%,booking_reference.ilike.%${filters.search}%`
      );
    }
    if (filters.status) {
      query = query.eq("status", filters.status);
    }
    if (filters.buildingId) {
      query = query.eq("organization_id", filters.buildingId);
    }
    if (filters.dateFrom) {
      query = query.gte("check_in", filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte("check_out", filters.dateTo);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("[admin/getBookings]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as Booking[], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getBookings]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getBookingById(id: string): Promise<QueryResult<Booking>> {
  try {
    const { data, error } = await supabase
      .from("bookings")
      .select(`
        *,
        unit:units ( *, organization:organizations ( id, name, address ) ),
        host:hosts (
          *,
          profile:profiles!hosts_user_id_fkey ( id, full_name, email, phone, avatar_url )
        ),
        guest_token:guest_tokens ( * )
      `)
      .eq("id", id)
      .maybeSingle();

    if (error) {
      console.error("[admin/getBookingById]", error);
      return { data: null, error: error.message };
    }
    return { data: data as Booking | null, error: null };
  } catch (err: any) {
    console.error("[admin/getBookingById]", err);
    return { data: null, error: err.message ?? "Unknown error" };
  }
}

// ─── Compliance ───────────────────────────────────────────────────────────────

export async function getComplianceRules(
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<StrataConfig>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    const { data, error, count } = await supabase
      .from("strata_config")
      .select(`
        *,
        organization:organizations ( id, name, address )
      `, { count: "exact" })
      .order("updated_at", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("[admin/getComplianceRules]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as StrataConfig[], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getComplianceRules]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getViolations(
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<Host>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    // Violation: nights_used > night_limit OR status = suspended/expired
    const { data, error, count } = await supabase
      .from("hosts")
      .select(`
        *,
        profile:profiles!hosts_user_id_fkey ( id, full_name, email, avatar_url ),
        unit:units (
          id, unit_number, status,
          organization:organizations ( id, name )
        )
      `, { count: "exact" })
      .or("status.eq.suspended,status.eq.expired")
      .order("updated_at", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("[admin/getViolations]", error);
      return { data: [], error: error.message };
    }

    // Also include hosts over their night cap
    const { data: overCapData, error: overCapErr } = await supabase
      .from("hosts")
      .select(`
        *,
        profile:profiles!hosts_user_id_fkey ( id, full_name, email, avatar_url ),
        unit:units (
          id, unit_number, status,
          organization:organizations ( id, name )
        )
      `)
      .filter("nights_used", "gt", 0);

    const overCap = overCapErr
      ? []
      : (overCapData ?? []).filter((h: any) => h.nights_used > h.night_limit);

    const combined = [...(data ?? [])];
    overCap.forEach((h: any) => {
      if (!combined.find((c: any) => c.id === h.id)) combined.push(h);
    });

    return { data: combined as Host[], error: null, count: (count ?? 0) + overCap.length };
  } catch (err: any) {
    console.error("[admin/getViolations]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

// ─── Audit Logs ───────────────────────────────────────────────────────────────

export interface AuditLogFilters {
  search?: string;
  action?: string;
  entityType?: string;
  dateFrom?: string;
  dateTo?: string;
  ip?: string;
}

export async function getAuditLogs(
  filters: AuditLogFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<AuditLog>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    let query = supabase
      .from("audit_logs")
      .select(`
        *,
        actor:profiles!audit_logs_user_id_fkey (
          id, full_name, email, role
        )
      `, { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (filters.search) {
      query = query.or(
        `action.ilike.%${filters.search}%,detail.ilike.%${filters.search}%,user_name.ilike.%${filters.search}%`
      );
    }
    if (filters.action) {
      query = query.ilike("action", `%${filters.action}%`);
    }
    if (filters.entityType) {
      query = query.ilike("entity", `%${filters.entityType}%`);
    }
    if (filters.dateFrom) {
      query = query.gte("created_at", filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte("created_at", filters.dateTo);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("[admin/getAuditLogs]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as AuditLog[], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getAuditLogs]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

// ─── Subscriptions ────────────────────────────────────────────────────────────

export interface SubscriptionFilters {
  plan?: string;
  status?: string;
}

export async function getSubscriptions(
  filters: SubscriptionFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<any>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    let query = supabase
      .from("subscriptions")
      .select(`
        *,
        profile:profiles!subscriptions_user_id_fkey (
          id, full_name, email, role
        )
      `, { count: "exact" })
      .order("updated_at", { ascending: false })
      .range(from, to);

    if (filters.status) {
      query = query.eq("status", filters.status);
    }
    if (filters.plan) {
      query = query.ilike("price_id", `%${filters.plan}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("[admin/getSubscriptions]", error);
      return { data: [], error: error.message };
    }
    return { data: data ?? [], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getSubscriptions]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getInvoices(
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<any>> {
  try {
    // Invoices live in the stripe_customers table or are fetched via Edge Function.
    // Here we return subscription records with payment-related fields as a proxy.
    const [from, to] = pageRange(page, pageSize);

    const { data, error, count } = await supabase
      .from("subscriptions")
      .select(`
        *,
        profile:profiles!subscriptions_user_id_fkey (
          id, full_name, email
        ),
        stripe_customer:stripe_customers!stripe_customers_user_id_fkey (
          id, stripe_customer_id
        )
      `, { count: "exact" })
      .not("stripe_subscription_id", "is", null)
      .order("updated_at", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("[admin/getInvoices]", error);
      return { data: [], error: error.message };
    }
    return { data: data ?? [], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getInvoices]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

// ─── Messages ─────────────────────────────────────────────────────────────────

export interface MessageFilters {
  buildingId?: string;
  userId?: string;
  unread?: boolean;
}

export async function getMessages(
  filters: MessageFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<Message>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    let query = supabase
      .from("messages")
      .select(`
        *,
        sender:profiles!messages_sender_id_fkey (
          id, full_name, email, role, avatar_url
        )
      `, { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (filters.buildingId) {
      query = query.eq("organization_id", filters.buildingId);
    }
    if (filters.userId) {
      query = query.or(
        `sender_id.eq.${filters.userId},recipient_ids.cs.{${filters.userId}}`
      );
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("[admin/getMessages]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as Message[], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getMessages]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

export async function getMessageThread(threadId: string): Promise<QueryResult<Message>> {
  try {
    const { data, error } = await supabase
      .from("messages")
      .select(`
        *,
        sender:profiles!messages_sender_id_fkey (
          id, full_name, email, role, avatar_url
        )
      `)
      .eq("id", threadId)
      .maybeSingle();

    if (error) {
      console.error("[admin/getMessageThread]", error);
      return { data: null, error: error.message };
    }
    return { data: data as Message | null, error: null };
  } catch (err: any) {
    console.error("[admin/getMessageThread]", err);
    return { data: null, error: err.message ?? "Unknown error" };
  }
}

// ─── Announcements ────────────────────────────────────────────────────────────

export async function getAnnouncements(
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE
): Promise<QueryListResult<any>> {
  try {
    const [from, to] = pageRange(page, pageSize);

    const { data, error, count } = await supabase
      .from("announcements")
      .select(`
        *,
        author:profiles!announcements_author_id_fkey (
          id, full_name, email, role
        )
      `, { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    if (error) {
      console.error("[admin/getAnnouncements]", error);
      return { data: [], error: error.message };
    }
    return { data: data ?? [], error: null, count: count ?? 0 };
  } catch (err: any) {
    console.error("[admin/getAnnouncements]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}

// ─── Settings ─────────────────────────────────────────────────────────────────

export async function getPlatformSettings(): Promise<QueryResult<any>> {
  try {
    const { data, error } = await supabase
      .from("platform_settings")
      .select("*")
      .maybeSingle();

    if (error) {
      console.error("[admin/getPlatformSettings]", error);
      return { data: null, error: error.message };
    }
    return { data, error: null };
  } catch (err: any) {
    console.error("[admin/getPlatformSettings]", err);
    return { data: null, error: err.message ?? "Unknown error" };
  }
}

export async function getFeatureFlags(): Promise<QueryResult<any>> {
  try {
    const { data, error } = await supabase
      .from("feature_flags")
      .select("*")
      .order("flag_name", { ascending: true });

    if (error) {
      console.error("[admin/getFeatureFlags]", error);
      return { data: null, error: error.message };
    }
    return { data, error: null };
  } catch (err: any) {
    console.error("[admin/getFeatureFlags]", err);
    return { data: null, error: err.message ?? "Unknown error" };
  }
}

export async function getAdminUsers(): Promise<QueryListResult<Profile>> {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("role", "system_admin")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[admin/getAdminUsers]", error);
      return { data: [], error: error.message };
    }
    return { data: (data ?? []) as Profile[], error: null };
  } catch (err: any) {
    console.error("[admin/getAdminUsers]", err);
    return { data: [], error: err.message ?? "Unknown error" };
  }
}
